import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import FormGroup from "@mui/material/FormGroup";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import Autocomplete from "@mui/material/Autocomplete";
import { Typography, Stack } from "@mui/material";
import { insertCommas } from "../utils/formatInput";
import { GradienCheckCircleIcon, GradienCancelIcon, GradienFirstMonthIcon } from "./icons/icon-gradient";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";


export const BillFormContent = ({
  billData,
  index,
  register,
  errors,
  setValue,
  watch,
  assets,
  isLoadingAssets,
  onAssetsDropdownOpen,
  setIsOpenDialog,
  _emissions,
  mode
}) => {
  // Field name prefix for this bill in the array
  const fieldPrefix = `bills.${index}`;

  // Watch form values for this bill
  const watchedValues = watch(fieldPrefix) || {};

  // Helper to get field value (from form or fallback to billData)
  const getFieldValue = (field, defaultValue = "") => {
    return watchedValues[field] ?? defaultValue;
  };

  // Helper to update field value
  const updateField = (field, value) => {
    setValue(`${fieldPrefix}.${field}`, value, { shouldDirty: true });
  };

  // Common text field styles
  const textFieldSx = {
    ".MuiInputBase-root": {
      width: "100%",
      "& > .MuiInputBase-input": {
        padding: "8px",
      },
    },
    ".MuiOutlinedInput-notchedOutline": {
      borderRadius: "8px",
    },
    ".Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: "#FF5B5B",
    },
  };

  const multilineTextFieldSx = {
    ".MuiInputBase-root": {
      width: "100%",
      padding: "4px 0",
      "& > .MuiInputBase-input": {
        padding: "8px",
      },
    },
    ".MuiOutlinedInput-notchedOutline": {
      borderRadius: "8px",
    },
    ".Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: "#FF5B5B",
    },
  };

  return (
    <Box sx={{ height: "100%" }}>
      <FormGroup>
        <Box
          display="grid"
          gridTemplateColumns={mode === 'liff' ? '1fr' : 'repeat(2, 1fr)'}
          gap={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="caption" color="textSecondary">
              Activity Date
            </Typography>
            <TextField
              {...register(`${fieldPrefix}.activity_date`, {
                required: "Activity date is required",
              })}
              value={getFieldValue('activity_date', billData?.activity_date ? billData.activity_date.split("T")[0] : "")}
              error={!!errors?.bills?.[index]?.activity_date}
              variant="outlined"
              type="date"
              fullWidth
              onChange={(e) => updateField('activity_date', e.target.value)}
              sx={textFieldSx}
            />
          </Box>

          <Box>
            <Typography variant="caption" color="textSecondary">
              Date
            </Typography>
            <TextField
              {...register(`${fieldPrefix}.date`, {
                required: "Date is required",
              })}
              value={getFieldValue('date', billData?.date ? billData.date.split("T")[0] : "")}
              error={!!errors?.bills?.[index]?.date}
              type="date"
              variant="outlined"
              fullWidth
              onChange={(e) => updateField('date', e.target.value)}
              sx={textFieldSx}
            />
          </Box>
        </Box>

        <Box
          display="grid"
          gridTemplateColumns={mode === 'liff' ? '1fr' : 'repeat(2, 1fr)'}
          gap={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="caption" color="textSecondary">
              Bill Type
            </Typography>
            <TextField
              {...register(`${fieldPrefix}.bill_type`, {
                required: "Bill type is required",
              })}
              value={getFieldValue('bill_type', billData?.bill_type || "")}
              error={!!errors?.bills?.[index]?.bill_type}
              fullWidth
              onChange={(e) => updateField('bill_type', e.target.value)}
              sx={textFieldSx}
            />
          </Box>

          <Box>
            <Typography variant="caption" color="textSecondary">
              Asset Type
            </Typography>
            <TextField
              {...register(`${fieldPrefix}.asset_type_name`, {
                required: "Asset type is required",
              })}
              value={getFieldValue('asset_type_name', billData?.asset_type_name || "")}
              error={!!errors?.bills?.[index]?.asset_type_name}
              variant="outlined"
              fullWidth
              onChange={(e) => updateField('asset_type_name', e.target.value)}
              sx={textFieldSx}
            />
          </Box>
        </Box>

        <Box
          display="grid"
          gridTemplateColumns={mode === 'liff' ? '1fr' : 'repeat(2, 1fr)'}
          gap={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="caption" color="textSecondary">
              Invoice ID
            </Typography>
            <TextField
              {...register(`${fieldPrefix}.invoice_id`, {
                required: "Invoice ID is required",
              })}
              value={getFieldValue('invoice_id', billData?.invoice_id || "")}
              error={!!errors?.bills?.[index]?.invoice_id}
              variant="outlined"
              fullWidth
              onChange={(e) => updateField('invoice_id', e.target.value)}
              sx={textFieldSx}
            />
          </Box>

          <Box>
            <Typography variant="caption" color="textSecondary">
              Tax ID
            </Typography>
            <TextField
              {...register(`${fieldPrefix}.tax_id`, {
                required: "Tax ID is required",
              })}
              value={getFieldValue('tax_id', billData?.tax_id || "")}
              error={!!errors?.bills?.[index]?.tax_id}
              onChange={(e) => updateField('tax_id', e.target.value)}
              variant="outlined"
              fullWidth
              sx={textFieldSx}
            />
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="textSecondary">
            Address
          </Typography>
          <TextField
            {...register(`${fieldPrefix}.address`, {
              required: "Address is required",
            })}
            value={getFieldValue('address', billData?.address || "")}
            error={!!errors?.bills?.[index]?.address}
            variant="outlined"
            fullWidth
            multiline
            maxRows={3}
            onChange={(e) => updateField('address', e.target.value)}
            sx={multilineTextFieldSx}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="textSecondary">
            Supplier Name
          </Typography>
          <TextField
            {...register(`${fieldPrefix}.supplier_name`, {
              required: "Supplier name is required",
            })}
            value={getFieldValue('supplier_name', billData?.supplier_name || "")}
            error={!!errors?.bills?.[index]?.supplier_name}
            variant="outlined"
            fullWidth
            onChange={(e) => updateField('supplier_name', e.target.value)}
            sx={textFieldSx}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="textSecondary">
            Supplier Tax ID
          </Typography>
          <TextField
            {...register(`${fieldPrefix}.supplier_tax_id`, {
              required: "Supplier tax ID is required",
            })}
            value={getFieldValue('supplier_tax_id', billData?.supplier_tax_id || "")}
            error={!!errors?.bills?.[index]?.supplier_tax_id}
            onChange={(e) => updateField('supplier_tax_id', e.target.value)}
            variant="outlined"
            fullWidth
            sx={textFieldSx}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="textSecondary">
            Supplier Address
          </Typography>
          <TextField
            {...register(`${fieldPrefix}.supplier_address`, {
              required: "Supplier address is required",
            })}
            value={getFieldValue('supplier_address', billData?.supplier_address || "")}
            error={!!errors?.bills?.[index]?.supplier_address}
            variant="outlined"
            multiline
            maxRows={3}
            fullWidth
            onChange={(e) => updateField('supplier_address', e.target.value)}
            sx={multilineTextFieldSx}
          />
        </Box>
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="caption" color="textSecondary">Bill to Contractor</Typography>
          {billData?.is_contractor_billed === false ? (
            <GradienCancelIcon fontSize="25px" />
          ) : (
            <GradienCheckCircleIcon fontSize="25px" />
          )}
        </Box>
      </FormGroup>

      <FormGroup>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: "bold" }}>
          Bill Transaction(s)
        </Typography>

        <Stack gap={1}>
          {(watchedValues?.transactions || billData?.details || []).map((detail, transIndex) => {
            const transFieldPrefix = `${fieldPrefix}.transactions.${transIndex}`;
            const watchedTrans = watchedValues?.transactions?.[transIndex] || {};
            
            return (
              <Box
                key={`transaction-${index}-detail-${transIndex}`}
                sx={{
                  p: 1,
                  border: "1px solid rgba(0, 0, 0, 0.23)",
                  borderRadius: "8px",
                }}
              >
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ mb: 1 }}
                >
                  Transaction ID:{" "}
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: "bold" }}
                  >
                    {detail?.transaction_dap_id}
                  </Typography>
                </Typography>

                {/* Hidden field for detail id */}
                <input type="hidden" {...register(`${transFieldPrefix}.id`)} />
                <input type="hidden" {...register(`${transFieldPrefix}.transaction_dap_id`)} />

                <Box
                  display="grid"
                  gridTemplateColumns="repeat(2, 1fr)"
                  gap={2}
                  sx={{ mb: 1 }}
                >
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Confidence
                    </Typography>
                    <FormControl sx={{ display: "block", width: "100%" }}>
                      <OutlinedInput
                        fullWidth
                        {...register(`${transFieldPrefix}.confidence`)}
                        value={watchedTrans?.confidence ?? detail?.confidence ?? ""}
                        endAdornment={
                          <InputAdornment position="end">
                            %
                          </InputAdornment>
                        }
                        sx={{
                          borderRadius: "8px",
                          "& > .MuiInputBase-input": {
                            padding: "8px",
                          },
                        }}
                        type="number"
                        inputProps={{
                          step: "0.01",
                          min: "0",
                          max: "100",
                          readOnly: true,
                        }}
                      />
                    </FormControl>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Unit {watchedTrans?.convert_unit ?? detail?.convert_unit}
                    </Typography>
                    <TextField
                      {...register(`${transFieldPrefix}.unit`)}
                      variant="outlined"
                      value={
                        watchedTrans?.unit !== undefined
                          ? (watchedTrans?.unit === "" ? "" : insertCommas(String(watchedTrans?.unit)))
                          : (detail?.unit ? insertCommas(String(detail?.unit)) : "")
                      }
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9.-]/g, "").replace(/-/g, (match, offset) => (offset === 0 ? match : ""));
                        const [integerPart, decimalPart] = value.split(".");
                        let result = insertCommas(integerPart);
                        if (decimalPart !== undefined) {
                          result += `.${decimalPart}`;
                        } else if (value.includes(".")) {
                          result += ".";
                        }
                        const numericValue = result ? parseFloat(result.replace(/,/g, "")) : "";
                        setValue(`${transFieldPrefix}.unit`, numericValue, { shouldDirty: true });
                      }}
                      sx={{
                        display: "block",
                        ".MuiInputBase-root": {
                          width: "100%",
                          "& > .MuiInputBase-input": {
                            padding: "8px",
                          },
                        },
                        ".MuiOutlinedInput-notchedOutline": {
                          borderRadius: "8px",
                        },
                      }}
                    />
                  </Box>
                </Box>

                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Assets
                  </Typography>
                  <Box className="w-full flex items-center gap-2">
                    <Autocomplete
                      options={assets}
                      loading={isLoadingAssets}
                      disableClearable
                      onOpen={() => onAssetsDropdownOpen(billData?.data_source_transaction_detail, true)}
                      getOptionLabel={(option) =>
                        option.title !== "Create new asset"
                          ? option.title
                          : ""
                      }
                      onChange={(_, newValue) => {
                        if (newValue?.title === "Create new asset") {
                          setIsOpenDialog(true);
                          return;
                        }
                        setValue(`${transFieldPrefix}.plant_id`, newValue?.plant_id ?? null, { shouldDirty: true });
                        setValue(`${transFieldPrefix}.asset_title`, newValue?.title ?? "", { shouldDirty: true });
                        setValue(`${transFieldPrefix}.asset_en`, newValue?.nameEnglish ?? "", { shouldDirty: true });
                        setValue(`${transFieldPrefix}.asset_th`, newValue?.nameThai ?? "", { shouldDirty: true });
                        setValue(`${transFieldPrefix}.license_plate`, newValue?.licensePlate ?? "", { shouldDirty: true });
                      }}
                      value={(assets || []).find(asset => 
                        asset?.plant_id === (watchedTrans?.plant_id ?? detail?.plant_id) && 
                        asset?.title === (watchedTrans?.asset_title || detail?.asset_title || detail?.title)
                      ) || ((watchedTrans?.plant_id ?? detail?.plant_id) ? { 
                        plant_id: watchedTrans?.plant_id ?? detail?.plant_id, 
                        title: watchedTrans?.asset_title || detail?.asset_title || detail?.title 
                      } : null)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder={isLoadingAssets ? "Loading assets..." : "Select asset"}
                          inputProps={{
                            ...params.inputProps,
                            readOnly: true,
                            style: { cursor: 'pointer' },
                          }}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {isLoadingAssets ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                                    <Box
                                      sx={{
                                        width: 16,
                                        height: 16,
                                        border: '2px solid #e0e0e0',
                                        borderTop: '2px solid #3dbb89',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite',
                                        '@keyframes spin': {
                                          '0%': { transform: 'rotate(0deg)' },
                                          '100%': { transform: 'rotate(360deg)' },
                                        },
                                      }}
                                    />
                                  </Box>
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box
                          key={`asset-${option?.title}`}
                          {...props}
                          sx={{
                            ...(option?.title === "Create new asset" && {
                              borderBottom: "1px solid #3dbb89",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              "&:hover": {
                                backgroundColor: "#eef7f5 !important",
                              },
                            }),
                          }}
                        >
                          {option?.title === "Create new asset" && (
                            <AddOutlinedIcon
                              sx={{ fontSize: "16px", color: "#3dbb89" }}
                            />
                          )}
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ fontWeight: 400 }}
                          >
                            {option.title}
                          </Typography>
                        </Box>
                      )}
                      loadingText="Loading assets..."
                      noOptionsText={isLoadingAssets ? "Loading..." : "No assets found"}
                      isOptionEqualToValue={(option, value) =>
                        option?.plant_id === value?.plant_id && option?.title === value?.title
                      }
                      sx={{
                        flexGrow: 1,
                        ".MuiInputBase-root": {
                          p: "4px",
                        },
                      }}
                    />
                  </Box>
                </Box>

                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Emissions
                  </Typography>
                  <Box className="w-full flex items-center gap-2">
                    <Autocomplete
                      options={_emissions}
                      getOptionLabel={(option) => option.emission_list || ""}
                      disableClearable
                      onChange={(_, newValue) => {
                        setValue(`${transFieldPrefix}.emission_list`, newValue?.emission_list ?? "", { shouldDirty: true });
                      }}
                      value={(_emissions || []).find(emission => 
                        emission?.emission_list === (watchedTrans?.emission_list ?? detail?.emission_list)
                      ) || ((watchedTrans?.emission_list ?? detail?.emission_list) ? {
                        emission_list: watchedTrans?.emission_list ?? detail?.emission_list
                      } : null)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Select emission"
                          inputProps={{
                            ...params.inputProps,
                            readOnly: true,
                            style: { cursor: 'pointer' },
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box key={`emission-${option?.id}`} {...props}>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ fontWeight: 400 }}
                          >
                            {option.emission_list}
                          </Typography>
                        </Box>
                      )}
                      isOptionEqualToValue={(option, value) =>
                        option?.emission_list === value?.emission_list
                      }
                      sx={{
                        flexGrow: 1,
                        ".MuiInputBase-root": {
                          p: "4px",
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Stack>
      </FormGroup>
    </Box>
  );
};
